import { useNavigate, type NavigateFunction } from "react-router-dom";
import { useLocation, useRoutes } from "react-router-dom";
import { useEffect } from "react";
import routes from "./config";
import { scrollToHash } from "@/utils/hashScroll";

let navigateResolver: (navigate: ReturnType<typeof useNavigate>) => void;

declare global {
  interface Window {
    REACT_APP_NAVIGATE: ReturnType<typeof useNavigate>;
  }
}

export const navigatePromise = new Promise<NavigateFunction>((resolve) => {
  navigateResolver = resolve;
});

export function AppRoutes() {
  const element = useRoutes(routes);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    window.REACT_APP_NAVIGATE = navigate;
    navigateResolver(window.REACT_APP_NAVIGATE);
  });

  useEffect(() => {
    const handleHashLinkClick = (event: globalThis.MouseEvent) => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.altKey ||
        event.ctrlKey ||
        event.shiftKey
      ) {
        return;
      }

      const anchor = (event.target as HTMLElement).closest<HTMLAnchorElement>("a[href]");
      if (!anchor || anchor.target || anchor.hasAttribute("download")) return;

      const href = anchor.getAttribute("href");
      if (!href || href.startsWith("tel:") || href.startsWith("mailto:")) return;

      const url = new URL(anchor.href, window.location.href);
      if (url.origin !== window.location.origin || !url.hash) return;

      const targetPath = url.pathname || "/";
      const currentPath = window.location.pathname;
      const basePath = targetPath === currentPath ? "" : targetPath;
      const nextUrl = `${basePath}${url.search}${url.hash}` || url.hash;

      event.preventDefault();

      if (targetPath === currentPath && url.search === window.location.search) {
        window.history.pushState(null, "", `${url.search}${url.hash}`);
        scrollToHash(url.hash);
        return;
      }

      navigate(nextUrl);
    };

    document.addEventListener("click", handleHashLinkClick, { capture: true });
    return () => document.removeEventListener("click", handleHashLinkClick, { capture: true });
  }, [navigate]);

  useEffect(() => {
    if (!location.hash) {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      return;
    }

    const timer = window.setTimeout(() => {
      scrollToHash(location.hash);
    }, 50);

    return () => window.clearTimeout(timer);
  }, [location.pathname, location.hash]);

  return element;
}
